'use client';

import { FileText } from 'lucide-react';
import { useLocale } from 'next-intl';
import { useMemo } from 'react';

import { Button } from '@/components/ui/button';
import { getGroundedWorkUiCopy } from '@/i18n/grounded-work-ui-copy';
import {
  groundedWorkSections,
  parseGroundedWorkDocument,
} from '@/lib/grounded-work-document';
import {
  mediaContextCtaKind,
  naturalDocumentName,
  selectCreatedForMediaContext,
  shouldShowLinkedSectionNotice,
} from '@/lib/grounded-work-media-context';
import { cn } from '@/lib/utils';
import { api, useQuery } from '@/lib/convex';
import { useAuthorizedWorkspaceMarkdown } from '@/components/chat/tool-calls/use-authorized-workspace-markdown';

export function GroundedWorkMediaContextBanner({
  className,
  fallbackDocumentPath,
  onOpenWorkspacePath,
  orgSlug,
  sourcePath,
}: {
  className?: string;
  fallbackDocumentPath?: string | null;
  onOpenWorkspacePath?: (path: string) => void;
  orgSlug: string;
  sourcePath: string;
}) {
  const copy = getGroundedWorkUiCopy(useLocale());
  const relationsResult = useQuery(
    api.vfs.file_relations.listForPath,
    orgSlug && sourcePath ? { orgSlug, path: sourcePath, limit: 8 } : 'skip'
  );
  const relationContext = useMemo(
    () =>
      selectCreatedForMediaContext({
        relations: relationsResult.data ?? [],
        sourcePath,
      }),
    [relationsResult.data, sourcePath]
  );
  const context =
    relationContext ??
    (fallbackDocumentPath
      ? {
          contextChanged: false,
          documentName:
            fallbackDocumentPath.split('/').pop() ?? fallbackDocumentPath,
          documentPath: fallbackDocumentPath,
        }
      : null);
  const markdownKey = context
    ? `${context.documentPath}:${context.contextChanged ? 'changed' : 'current'}`
    : null;
  const markdownState = useAuthorizedWorkspaceMarkdown({
    enabled: Boolean(context),
    key: markdownKey,
    orgSlug,
    path: context?.documentPath,
  });
  const document = useMemo(
    () =>
      markdownState.text
        ? parseGroundedWorkDocument(markdownState.text, {
            path: context?.documentPath,
          })
        : null,
    [context?.documentPath, markdownState.text]
  );

  if (!context || (!relationContext && !document)) return null;
  const title = document?.title || naturalDocumentName(context.documentName);
  const targetItemId = context.itemId ?? context.anchor;
  const item = targetItemId
    ? document?.sections.find(
        (section) =>
          section.itemId === targetItemId || section.anchor === targetItemId
      )
    : null;
  const showLinkedSectionNotice = shouldShowLinkedSectionNotice({
    contextChanged: context.contextChanged,
    documentLoaded: Boolean(document),
    itemResolved: Boolean(item),
    ...(targetItemId ? { targetItemId } : {}),
  });
  const hasResearch = Boolean(
    document && groundedWorkSections(document, 'evidence').length > 0
  );
  const ctaKind = mediaContextCtaKind({
    hasContext: Boolean(document?.sections.length),
    hasResearch,
  });
  const cta =
    ctaKind === 'research-and-rationale'
      ? copy.viewResearchAndRationale
      : ctaKind === 'context-and-rationale'
        ? copy.viewContextAndRationale
        : copy.viewRationale;

  return (
    <aside
      data-grounded-media-context=""
      className={cn(
        'border-border/65 bg-background/92 absolute top-3 left-3 z-30 max-w-[min(24rem,calc(100%-1.5rem))] rounded-lg border px-3 py-2.5 shadow-md backdrop-blur-sm',
        className
      )}
    >
      <div className="flex items-start gap-2.5">
        <FileText
          className="text-muted-foreground mt-0.5 size-4 shrink-0"
          aria-hidden="true"
        />
        <div className="min-w-0">
          <div className="text-foreground truncate text-xs font-medium">
            {copy.createdFor.replace('{title}', title)}
          </div>
          {item?.heading || context.mediaRole ? (
            <div className="text-muted-foreground mt-0.5 truncate text-xs">
              {[item?.heading, context.mediaRole].filter(Boolean).join(' · ')}
            </div>
          ) : null}
          {showLinkedSectionNotice ? (
            <div className="text-muted-foreground mt-1 text-xs leading-tight">
              {copy.linkedSectionMayHaveMoved}
            </div>
          ) : null}
          {onOpenWorkspacePath ? (
            <Button
              type="button"
              variant="link"
              size="sm"
              className="mt-1 h-auto p-0 text-xs"
              onClick={() => onOpenWorkspacePath(context.documentPath)}
            >
              {cta}
            </Button>
          ) : null}
        </div>
      </div>
    </aside>
  );
}
